import { z } from "zod";

/**
 * #449, #650 - the one implementation of how Starsach writes a date, a time or a number for a person
 * (crmarr/templates localization.md, transactional-email-content.md rule 10). The portal's `useFormat()` and the
 * email the notifications dispatcher renders both call these, so a reader sees the same text in the portal and in
 * their inbox.
 *
 * Everything here takes a **resolved** format: a real IANA zone, a date format, a 12- or 24-hour clock. Turning a
 * preference of `auto` into one is the caller's, because it means different things where there is a device (the
 * portal: the device's zone, English in the device's region) and where there is not (a Lambda: `recipientFormat`).
 */

/**
 * `us` / `intl` are numeric (`MM/DD/YYYY`, `DD/MM/YYYY`); `us-text` (`Oct 10, 2026`) and `intl-text`
 * (`10 Oct 2026`) name the month (#449). Values are only ever added, so every stored one stays valid.
 */
export const DateFormatPreferenceSchema = z.enum(["auto", "iso", "us", "intl", "us-text", "intl-text"]);
/**
 * The clock, its own choice (#449): `auto` is the hour cycle of the UI language taken in the device's region, never
 * derived from the date format; `12h` always reads AM/PM.
 */
export const TimeFormatPreferenceSchema = z.enum(["auto", "12h", "24h"]);

export type DateFormat = z.infer<typeof DateFormatPreferenceSchema>;
export type Clock = "12h" | "24h";

export interface ResolvedFormat {
  /** An IANA zone. */
  timeZone: string;
  /**
   * The user's date format, or `long` - the written-out form a server uses for `auto`, where there is no device to
   * ask: `Wednesday, September 30, 2026`. Never a preference a person picks.
   */
  dateFormat: DateFormat | "long";
  clock: Clock;
  /** The locale `auto` dates and numbers follow; undefined is the runtime's (the device's, in a browser). */
  locale?: string | undefined;
}

export interface WhenOptions {
  /** Seconds as well. */
  seconds?: boolean;
}

type Value = string | number | Date;
type Parts = Record<string, string>;

/** The words inside a date (month names) are the UI's language, which is English. `en-US` says `Sep`, not `Sept`. */
const WORDS = "en-US";
/** The portal's one UI language. Its hour cycle - in the device's region, where there is one - is what `auto` follows. */
export const UI_LOCALE = "en";

const pad = (n: number) => String(n).padStart(2, "0");
const toDate = (value: Value) => (value instanceof Date ? value : new Date(value));

function parts(d: Date, timeZone: string): Parts {
  const f = new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23" });
  const out: Parts = {};
  for (const x of f.formatToParts(d)) out[x.type] = x.value;
  // Some engines print midnight as "24".
  if (out["hour"] === "24") out["hour"] = "00";
  return out;
}

/** A month as a word inside a date (`Oct`, or `October` for the long form); only the word is taken, the order is the format's. */
function monthWord(d: Date, timeZone: string, month: "short" | "long" = "short"): string {
  return new Intl.DateTimeFormat(WORDS, { timeZone, year: "numeric", month, day: "numeric" }).formatToParts(d).find((x) => x.type === "month")?.value ?? "";
}

/**
 * The tag whose hour cycle `timeFormat: auto` follows (crmarr/templates localization.md, Stugum #941): the UI
 * language, taken in the device's region when the device speaks that language - `en` on an `en-GB` device reads
 * `en-GB` (24-hour), on an `en-US` device `en-US` (12-hour). A bare `en` never gives 24-hour on its own, and a
 * device that does not speak English lends no region. A tag that already names a region is kept.
 */
export function clockLocale(uiLocale: string | undefined, device: readonly string[]): string {
  const ui = uiLocale || UI_LOCALE;
  if (ui.includes("-")) return ui;
  const primary = ui.toLowerCase();
  return device.find((tag) => tag.toLowerCase().split("-")[0] === primary) ?? ui;
}

/** A locale's own clock: `h11` / `h12` are 12-hour, `h23` / `h24` 24-hour. */
export function localeHourCycle(locale: string): Clock {
  try {
    const { hourCycle } = new Intl.DateTimeFormat(locale, { hour: "numeric" }).resolvedOptions();
    return hourCycle === "h11" || hourCycle === "h12" ? "12h" : "24h";
  } catch {
    return "24h";
  }
}

/** `2:05 PM` - always AM/PM, never a locale's `a. m.` (the owner's decision, 2026-09-26). */
function clock12(x: Parts, opts: WhenOptions): string {
  const h = Number(x["hour"]);
  return `${((h + 11) % 12) + 1}:${x["minute"]}${opts.seconds ? `:${x["second"]}` : ""} ${h < 12 ? "AM" : "PM"}`;
}

function clock24(x: Parts, opts: WhenOptions): string {
  return `${pad(Number(x["hour"]))}:${x["minute"]}${opts.seconds ? `:${x["second"]}` : ""}`;
}

const clock = (x: Parts, f: ResolvedFormat, opts: WhenOptions) => (f.clock === "12h" ? clock12(x, opts) : clock24(x, opts));

/** The date in the chosen format, from parts already in the right zone. `d` and `timeZone` name the month. */
function dateOf(d: Date, x: Parts, f: ResolvedFormat, timeZone: string): string {
  switch (f.dateFormat) {
    case "auto":
      return new Intl.DateTimeFormat(f.locale, { timeZone, year: "numeric", month: "numeric", day: "numeric" }).format(d);
    case "long":
      return new Intl.DateTimeFormat(WORDS, { timeZone, weekday: "long", year: "numeric", month: "long", day: "numeric" }).format(d);
    case "iso":
      return `${x["year"]}-${x["month"]}-${x["day"]}`;
    case "us":
      return `${x["month"]}/${x["day"]}/${x["year"]}`;
    case "intl":
      return `${x["day"]}/${x["month"]}/${x["year"]}`;
    case "us-text":
      return `${monthWord(d, timeZone)} ${Number(x["day"])}, ${x["year"]}`;
    case "intl-text":
      return `${Number(x["day"])} ${monthWord(d, timeZone)} ${x["year"]}`;
  }
}

/**
 * An instant in the format's zone, date format and clock. The date comes from `dateFormat`, the clock from `clock`,
 * for every date format. **The separator follows the clock** (the owner's decision, 2026-09-26): a comma before a
 * 12-hour time, a plain space before a 24-hour one, whatever the date format, seconds included -
 * `09/26/2026, 2:05 PM` · `09/26/2026 14:05` · `26/09/2026, 2:05 PM` · `2026-09-26, 2:05 PM` · `Sep 26, 2026 14:05`.
 * An unparseable value comes back as given.
 */
export function formatWhen(value: Value, f: ResolvedFormat, opts: WhenOptions = {}): string {
  const d = toDate(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const x = parts(d, f.timeZone);
  return `${dateOf(d, x, f, f.timeZone)}${f.clock === "12h" ? ", " : " "}${clock(x, f, opts)}`;
}

/** The date alone of an instant, in the format's zone. For a calendar date (`YYYY-MM-DD`) use formatDay. */
export function formatDate(value: Value, f: ResolvedFormat): string {
  const d = toDate(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return dateOf(d, parts(d, f.timeZone), f, f.timeZone);
}

/** Time of day alone, in the format's zone and clock - for events on one known day. */
export function formatTime(value: Value, f: ResolvedFormat, opts: WhenOptions = {}): string {
  const d = toDate(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return clock(parts(d, f.timeZone), f, opts);
}

const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * A calendar date - a day, not an instant - in the date format, never converted between zones: `2026-10-16` is the
 * 16th in Auckland and in Honolulu alike. An ISO instant is taken as its UTC date (a UTC day bucket, such as the
 * start of a range the API counted in UTC days). Anything else comes back as given.
 */
export function formatDay(value: string, f: ResolvedFormat): string {
  const m = CALENDAR_DATE.exec(value);
  if (!m) return value;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return dateOf(d, parts(d, "UTC"), f, "UTC");
}

/**
 * Month and day without the year, for chart ticks and day columns: ISO `09-14`, US `09/14`, international `14/09`,
 * US written `Sep 14`, international written `14 Sep`, long `September 14`, auto the locale's short form. A bare
 * `YYYY-MM-DD` is a calendar date and is not converted; any other value is an instant in the format's zone.
 */
export function formatMonthDay(value: Value, f: ResolvedFormat): string {
  const calendar = typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
  const d = calendar ? new Date(`${value}T00:00:00Z`) : toDate(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const timeZone = calendar ? "UTC" : f.timeZone;
  const x = parts(d, timeZone);
  switch (f.dateFormat) {
    case "auto":
      return new Intl.DateTimeFormat(f.locale, { timeZone, month: "short", day: "numeric" }).format(d);
    case "long":
      return `${monthWord(d, timeZone, "long")} ${Number(x["day"])}`;
    case "iso":
      return `${x["month"]}-${x["day"]}`;
    case "us":
      return `${x["month"]}/${x["day"]}`;
    case "intl":
      return `${x["day"]}/${x["month"]}`;
    case "us-text":
      return `${monthWord(d, timeZone)} ${Number(x["day"])}`;
    case "intl-text":
      return `${Number(x["day"])} ${monthWord(d, timeZone)}`;
  }
}

/** A calendar month (`YYYY-MM`) in the date format: ISO `2026-09`, numeric `09/2026`, written `Sep 2026`, long `September 2026`. */
export function formatMonth(value: string, f: ResolvedFormat): string {
  const m = /^(\d{4})-(\d{2})$/.exec(value);
  if (!m) return value;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1));
  switch (f.dateFormat) {
    case "auto":
      return new Intl.DateTimeFormat(f.locale, { timeZone: "UTC", month: "short", year: "numeric" }).format(d);
    case "long":
      return `${monthWord(d, "UTC", "long")} ${m[1]}`;
    case "iso":
      return value;
    case "us":
    case "intl":
      return `${m[2]}/${m[1]}`;
    case "us-text":
    case "intl-text":
      return `${monthWord(d, "UTC")} ${m[1]}`;
  }
}

/** A count with the locale's grouping (`12,345`). */
export function formatNumber(value: number, f: Pick<ResolvedFormat, "locale">, opts?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(f.locale, opts).format(value);
}

/** An amount in US dollars - every price and invoice line is USD (pricing.md). */
export function formatMoney(value: number, f: Pick<ResolvedFormat, "locale">, opts?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(f.locale, { style: "currency", currency: "USD", ...opts }).format(value);
}

const PLURALS = new Intl.PluralRules(WORDS);

/**
 * The word for a count - `plural(n, "contact", "contacts")` - chosen by Intl.PluralRules rather than `n === 1`,
 * so a second language later is a catalog, not a hunt through every sentence (localization.md decision 6).
 */
export function plural(n: number, one: string, other: string): string {
  return PLURALS.select(n) === "one" ? one : other;
}

/**
 * The zone's short name at that instant, so a time in a message says which zone it is in: `UTC`, `MDT`, `EST`, or
 * an offset where English has no abbreviation for it (`GMT+1` for London in summer, `GMT+5:30`).
 */
export function zoneName(value: Value, timeZone: string): string {
  const d = toDate(value);
  const name = Number.isNaN(d.getTime()) ? undefined : new Intl.DateTimeFormat(WORDS, { timeZone, timeZoneName: "short" }).formatToParts(d).find((x) => x.type === "timeZoneName")?.value;
  return name ?? timeZone;
}

/** True for a zone this runtime knows (`America/Denver`, `UTC`); false for anything else, `auto` included. */
export function isTimeZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat(WORDS, { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/** The three preferences a rendering reads; the rest of a person's preferences do not change how a date reads. */
export const FormatPreferencesSchema = z.object({
  /** An IANA zone, or "auto". */
  timeZone: z.string().min(1).max(64),
  dateFormat: DateFormatPreferenceSchema,
  timeFormat: TimeFormatPreferenceSchema,
});
export type FormatPreferences = z.infer<typeof FormatPreferencesSchema>;

/**
 * A recipient's format where there is no device - an email (#650; localization.md decision 5, "server-side Auto has
 * no device, so define it"): their saved zone, else UTC (Starsach keeps no organization zone, and never the
 * Lambda's); their date format, else the long written form; their clock, else English's hour cycle (12-hour).
 * Whatever comes back, the message names the zone beside every time.
 */
export function recipientFormat(p?: { [K in keyof FormatPreferences]?: FormatPreferences[K] | undefined } | null): ResolvedFormat {
  const zone = p?.timeZone && p.timeZone !== "auto" && isTimeZone(p.timeZone) ? p.timeZone : "UTC";
  const dateFormat = p?.dateFormat && p.dateFormat !== "auto" ? p.dateFormat : "long";
  const clockPref = p?.timeFormat === "12h" || p?.timeFormat === "24h" ? p.timeFormat : localeHourCycle(UI_LOCALE);
  return { timeZone: zone, dateFormat, clock: clockPref, locale: "en-US" };
}

/** An instant with its zone named, for a message: `Wednesday, September 30, 2026, 8:38 AM MDT`. */
export function formatWhenWithZone(value: Value, f: ResolvedFormat, opts: WhenOptions = {}): string {
  if (Number.isNaN(toDate(value).getTime())) return String(value);
  return `${formatWhen(value, f, opts)} ${zoneName(value, f.timeZone)}`;
}

/**
 * A `datetime-local` value (`2026-10-16T14:05`), read as a wall-clock time in `timeZone`, as the instant it names.
 * The input itself has no zone and the browser would read it in the device's, which is not the user's chosen one.
 * In a gap (spring forward) the time moves on by the gap; in an overlap it takes the earlier instant.
 */
export function wallTimeToInstant(local: string, timeZone: string): Date | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local);
  if (!m) return undefined;
  const asUtc = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  const offset = (t: number) => {
    const x = parts(new Date(t), timeZone);
    return Date.UTC(Number(x["year"]), Number(x["month"]) - 1, Number(x["day"]), Number(x["hour"]), Number(x["minute"]), Number(x["second"])) - t;
  };
  const first = asUtc - offset(asUtc);
  const second = asUtc - offset(first);
  const reads = (t: number) => t + offset(t) === asUtc;
  const exact = [first, second].filter(reads);
  return new Date(exact.length > 0 ? Math.min(...exact) : Math.max(first, second));
}
